import { Button } from '@/components/ui/button';
import { BuildingIcon, UserIcon } from '@/components/ui/icons';
import { Tag } from '@/components/ui/tag';
import ReassuranceSection from '@/components/shared/reassurance';
import { MOTION } from '@/utils';
import { PRICING_FEATURES, PRICING_PLANS } from '@/utils/data/princingFunctions';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef } from 'react';
import Link from 'next/link';

const cabinetPlan = PRICING_PLANS.find((p) => p.id === 'cabinet')!;
const collaborateurPlan = PRICING_PLANS.find((p) => p.id === 'collaborateur')!;

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className="h-3 w-3 shrink-0"
    fill="none"
    viewBox="0 0 12 12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 6.5L4.5 9L10 3.5"
      stroke="#ed630f"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.25"
    />
  </svg>
);

export default function Pricing() {
  const refs = {
    section: useRef(null),
    tag: useRef(null),
    title: useRef(null),
    subtitle: useRef(null),
    cardCabinet: useRef(null),
    cardCollab: useRef(null),
    featuresCabinet: useRef<(globalThis.HTMLLIElement | null)[]>([]),
    featuresCollab: useRef<(globalThis.HTMLLIElement | null)[]>([]),
  };

  useGSAP(
    () => {
      if (!refs.section.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });
      const splitSubtitle = new SplitText(refs.subtitle.current, { mask: 'lines', type: 'lines' });
      const featureEls = [
        ...refs.featuresCabinet.current.filter(Boolean),
        ...refs.featuresCollab.current.filter(Boolean),
      ];

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });

      const tl = gsap.timeline({
        defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .from(
          splitTitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.15',
        )
        .from(
          splitSubtitle.lines,
          { yPercent: MOTION.yPercent, stagger: MOTION.staggerLines },
          '<+0.1',
        )
        .from(refs.cardCabinet.current, { autoAlpha: 0, y: MOTION.y }, '<+0.2')
        .from(refs.cardCollab.current, { autoAlpha: 0, y: MOTION.y }, '<+0.15')
        .from(
          featureEls,
          { autoAlpha: 0, y: 10, stagger: 0.03, duration: MOTION.durationSm },
          '<+0.1',
        );

      return () => {
        splitTitle.revert();
        splitSubtitle.revert();
      };
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="max-width-blue-section flex flex-col items-center gap-12"
      id="tarif"
    >
      {/* En-tête */}
      <div className="flex w-full flex-col gap-3">
        <Tag ref={refs.tag}>Tarification</Tag>

        <h2 ref={refs.title} className="w-fit">
          Une tarification adaptée à chaque usage.
        </h2>

        <p className="sr-only">
          zLawyer accompagne aussi bien l&apos;activité du cabinet que celle de ses collaborateurs
          avec des offres pensées pour chaque besoin.
        </p>
        <p ref={refs.subtitle} aria-hidden="true" className="max-w-2xl text-left">
          zLawyer accompagne aussi bien l&apos;activité du cabinet que celle de ses collaborateurs
          avec des offres pensées pour chaque besoin.
        </p>
      </div>

      {/* Cartes */}
      <div className="flex w-full max-w-6xl flex-col gap-6">
        {/* Cabinet */}
        <div
          ref={refs.cardCabinet}
          className="border-dark-blue/[0.07] grid grid-cols-1 overflow-hidden rounded-(--radius-current) border bg-white shadow-(--shadow-current) lg:grid-cols-[320px_1fr]"
        >
          <div className="border-dark-blue/[0.07] flex flex-col justify-between gap-8 border-b p-8 lg:border-r lg:border-b-0 lg:p-10">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-orange rounded-md p-2 text-white">
                    <BuildingIcon />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-dark-blue font-roboto-medium text-sm uppercase">
                      Cabinet
                    </span>
                    <p className="text-orange font-roboto-medium text-sm! leading-snug">
                      zLawyer Cabinet
                    </p>
                  </div>
                </div>
                <p className="text-dark-blue/55 font-roboto-light text-sm! leading-relaxed">
                  La plateforme complète pour piloter l&apos;ensemble de l&apos;activité de votre
                  cabinet.
                </p>
              </div>

              <div className="flex flex-col gap-0.5">
                <span
                  className="text-orange font-roboto-regular leading-none tabular-nums"
                  style={{ fontSize: 'clamp(36px, 5vw, 48px)' }}
                >
                  {cabinetPlan.price}€
                </span>
                <span className="text-dark-blue/40 font-roboto-light text-sm">
                  /mois /utilisateur
                </span>
                <span className="text-dark-blue/35 font-roboto-light text-xs">Sans engagement</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-dark-blue/40 font-roboto-light text-xs! leading-relaxed">
                Accès complet pour tous les membres du cabinet : associés, collaborateurs, salariés.
              </p>
              <Button className="whitespace-nowrap" href="/contact" variant="fill">
                Demandez une démo
              </Button>
            </div>
          </div>

          <div className="p-6 lg:p-10">
            <ul className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
              {PRICING_FEATURES.map((feature, index) => (
                <li
                  key={feature.label}
                  ref={(el) => {
                    if (el) refs.featuresCabinet.current[index] = el;
                  }}
                  className="hover:bg-orange/10 flex items-center gap-2.5 rounded-md px-2 py-2"
                >
                  <CheckIcon />
                  <span className="text-dark-blue font-roboto-light text-sm! leading-snug">
                    {feature.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Collaborateur */}
        <div
          ref={refs.cardCollab}
          className="border-dark-blue/5 grid grid-cols-1 overflow-hidden rounded-(--radius-current) border bg-white lg:grid-cols-[320px_1fr]"
        >
          <div className="border-dark-blue/6 flex flex-col justify-between gap-8 border-b p-8 lg:border-r lg:border-b-0 lg:p-10">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <div className="border-orange text-orange rounded-md border bg-white p-2">
                    <UserIcon />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-dark-blue font-roboto-medium text-sm uppercase">
                      Collaborateur
                    </span>
                    <p className="text-orange font-roboto-medium text-sm! leading-snug">
                      zLawyer Collaborateur
                    </p>
                  </div>
                </div>
                <p className="text-dark-blue/55 font-roboto-light text-sm! leading-relaxed">
                  Déjà utilisateur zLawyer via votre cabinet ? Ouvrez votre propre compte pour gérer
                  votre activité indépendante et votre facturation électronique.
                </p>
              </div>

              <div className="flex flex-col gap-0.5">
                <span
                  className="text-dark-blue font-roboto-regular leading-none tabular-nums"
                  style={{ fontSize: 'clamp(36px, 5vw, 48px)' }}
                >
                  {collaborateurPlan.price}€
                </span>
                <span className="text-dark-blue/40 font-roboto-light text-sm">/mois</span>
                <span className="text-dark-blue/35 font-roboto-light text-xs">Sans engagement</span>
              </div>
            </div>

            <Button className="whitespace-nowrap" href="/tarif" variant="transparent">
              En savoir +
            </Button>
          </div>

          <div className="flex flex-col p-6 lg:p-10">
            <ul className="flex flex-col gap-0.5">
              {PRICING_FEATURES.filter((f) => f.inCollaborateur).map((feature, index) => (
                <li
                  key={feature.label}
                  ref={(el) => {
                    if (el) refs.featuresCollab.current[index] = el;
                  }}
                  className="hover:bg-dark-blue/5 flex items-center gap-2.5 rounded-md px-2 py-2"
                >
                  <CheckIcon />
                  <span className="text-dark-blue font-roboto-light text-sm leading-snug">
                    {feature.label}
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-dark-blue/8 bg-dark-blue/3 mt-6 rounded-xl border p-4">
              <p className="text-dark-blue/60 font-roboto-light text-xs! leading-relaxed">
                Réservé aux collaborateurs déjà utilisateurs de zLawyer via leur cabinet.
              </p>
              <Link
                className="text-orange font-roboto-medium inline-block text-xs hover:underline"
                href="/tarif"
              >
                Voir les offres complètes →
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full max-w-6xl">
        <ReassuranceSection />
      </div>
    </section>
  );
}
